import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ProfileSection } from './ProfileSection';
import { PasswordInput } from '@shared/components/ui/PasswordInput';
import { useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useProfile } from '../../hooks/useProfile';

export const Password = () => {
  const { t } = useTranslation('profileModule');
  const { toast } = useToast();
  const { updateUser } = useProfile();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: t('password.mismatch_title'),
        description: t('password.mismatch_description'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSaving(true);
      await updateUser({
        current_password: currentPassword,
        password: newPassword,
      });

      toast({
        title: t('password.success_title'),
        description: t('password.success_description'),
      });

      // Limpiar campos
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      // 1. Mensaje por defecto (genérico) - fallback de seguridad
      let errorMessage = t('password.error_description');

      // 2. Si hay mensaje específico del servidor, analizarlo
      if (error?.response?.data?.message) {
        const serverMessage = error.response.data.message;

        // 3. Determinar tipo específico de error según el contenido del mensaje
        if (serverMessage.includes('profile_photo_path') || serverMessage.includes('too long')) {
          // Error de URL corrupta o datos muy largos - problema del servidor
          errorMessage = 'Error en el servidor: Problema con datos del perfil. Contacta al administrador.';
        } else if (serverMessage.includes('current_password')) {
          // Error de contraseña actual incorrecta
          errorMessage = 'La contraseña actual es incorrecta.';
        } else if (serverMessage.includes('password')) {
          // Error relacionado con la nueva contraseña (formato, requisitos, etc.)
          errorMessage = 'Error al actualizar la contraseña. Verifica que cumpla con los requisitos.';
        } else {
          // 4. Si no coincide con ningún patrón específico, truncar mensajes largos
          // para evitar mostrar URLs corruptas o errores técnicos muy extensos
          errorMessage = serverMessage.length > 200 ? serverMessage.substring(0, 200) + '...' : serverMessage;
        }
      } else if (error?.message) {
        // 5. Si no hay mensaje del servidor pero hay error.message (errores de red, etc.)
        errorMessage = error.message;
      }
      // 6. Si no hay nada, se mantiene el mensaje genérico inicial

      toast({
        title: t('password.error_title'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ProfileSection
      title={t('password.title')}
      description={t('password.description')}
      className={`transition-opacity duration-300 ${isSaving ? 'pointer-events-none opacity-90' : ''}`}
      data-testid="profile-password-section"
    >
      <form onSubmit={handleSubmit}>
        <div className="mb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('password.current_label')}</label>
            <Input
              type="password"
              placeholder={t('password.current_placeholder')}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isSaving}
              required
            />
          </div>
          <p className="text-sm text-complementary-text">{t('password.hint')}</p>
          <div>
            <PasswordInput
              name="newPassword"
              label={t('password.new_label')}
              placeholder={t('password.new_placeholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSaving}
              required
            />
          </div>
          <div>
            <PasswordInput
              name="confirmPassword"
              label={t('password.confirm_label')}
              placeholder={t('password.confirm_placeholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSaving}
              required
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" className="w-full sm:w-auto" disabled={isSaving}>
            {isSaving ? t('password.saving') : t('password.submit')}
          </Button>
        </div>
      </form>
    </ProfileSection>
  );
};
